import { useQuery } from '@tanstack/react-query'
import { videoService } from '@/lib/api/services/video'
import type { PaginationParams, VideoFilterParams } from '@/lib/api/types'

// Query keys factory
export const videoKeys = {
  all: ['videos'] as const,
  folders: () => [...videoKeys.all, 'folders'] as const,
  searches: () => [...videoKeys.all, 'search'] as const,
  searchFolder: (params: PaginationParams, query: string) =>
    [...videoKeys.all, 'searchFolder', params, query] as const,
  filters: () => [...videoKeys.all, 'filter'] as const,
  filter: (params: PaginationParams, filters: VideoFilterParams) =>
    [...videoKeys.filters(), params, filters] as const,
  details: () => [...videoKeys.all, 'detail'] as const,
  detail: (id: string) => [...videoKeys.details(), id] as const,
  searchVideos: (params: PaginationParams, query: { searchTerm: string; folderId: string }) =>
    [...videoKeys.searches(), 'videos', params, query] as const,
  highlights: () => [...videoKeys.all, 'highlights'] as const,
}

/**
 * Hook to fetch video folders
 */
export const useVideoFolders = () => {
  return useQuery({
    queryKey: videoKeys.folders(),
    queryFn: () => videoService.getVideoFolders(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to search video folders by query
 */
export const useSearchVideoFolders = (
  query: string,
  params: PaginationParams
) => {
  return useQuery({
    queryKey: videoKeys.searchFolder(params, query),
    queryFn: () => videoService.searchVideoFolders(query, params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!query, // Only run this query if there is a search term
  })
}

/**
 * Hook to filter videos with pagination
 */
export const useFilterVideos = (
  params: PaginationParams,
  filters: VideoFilterParams = {}
) => {
  return useQuery({
    queryKey: videoKeys.filter(params, filters),
    queryFn: () => videoService.filterVideos(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to search videos by query with pagination
 */
export const useSearchVideos = (
  query: { searchTerm: string; folderId: string },
  params: PaginationParams
) => {
  return useQuery({
    queryKey: videoKeys.searchVideos(params, query),
    queryFn: () => videoService.searchVideos(query, params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!query.searchTerm, // Only run this query if there is a search term
  })
}

/**
 * Hook to get a single video by ID
 */
export const useVideoById = (videoUploadId: string) => {
  return useQuery({
    queryKey: videoKeys.detail(videoUploadId),
    queryFn: () => videoService.getVideoById(videoUploadId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!videoUploadId,
  })
}

/**
 * Hook to get home page highlights
 */
export const useVideoHighlights = () => {
  return useQuery({
    queryKey: videoKeys.highlights(),
    queryFn: () => videoService.getHomePageHighlights(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

